import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronRight, Music2, Palette, Pencil, PencilRuler, Plus } from 'lucide-react';
import type { NoteTemplate, NoteTemplatePresetKey, Subject } from '@repo/shared-types';
import { cn } from '@repo/ui/src/lib/utils';
import { AdminOnlyTooltip } from '../../components/AdminOnlyTooltip';
import { useCan } from '../../hooks/useCan';
import { NOTE_TEMPLATE_PRESETS, NOTE_TEMPLATE_PRESET_ORDER, presetWithFreshIds } from '../../lib/noteTemplatePresets';
import { toast } from 'sonner';
import { Switch } from '@repo/ui/src/components/ui/switch';
import { useUpdateSubject } from '../../hooks/useSubjects';
import { NoteTemplateEditor } from './NoteTemplateEditor';
import { isPristinePreset } from '../../lib/noteTemplatePresets';

const GROUPS = [
  { key: 'classItems', label: 'class' },
  { key: 'studentItems', label: 'student' },
  { key: 'homeworkItems', label: 'homework' },
] as const;

const PRESET_ICON: Record<NoteTemplatePresetKey, typeof Music2> = { music: Music2, art: Palette, exam: PencilRuler, custom: Plus };

const EMPTY_CUSTOM: NoteTemplate = { presetKey: 'custom', classItems: [], studentItems: [], homeworkItems: [] };

/**
 * E1 (handoff 19 §4): the "Sườn nhận xét" section on the subject page — read
 * mode (three-column card) or the empty state (dashed box + preset rows),
 * both opening the same editor. Picking a preset prefills the editor and saves
 * NOTHING until "Lưu sườn". Sits before the Học viên section: the page is the
 * subject's configuration and the template is configuration.
 */
export function NoteTemplateSection({ subject }: { subject: Subject }) {
  const { t } = useTranslation('progress');
  const canManage = useCan('manage:subjects');
  const [editorOpen, setEditorOpen] = useState(false);
  const [initial, setInitial] = useState<NoteTemplate>(EMPTY_CUSTOM);
  const updateSubject = useUpdateSubject(subject.id);
  const template = subject.noteTemplate;

  // A fresh clone every open: the editor re-seeds on identity, so handing it
  // the same object twice would keep a draft the user already discarded
  // (review I5).
  const openWith = (t0: NoteTemplate) => { setInitial(structuredClone(t0)); setEditorOpen(true); };

  const meta = useMemo(() => {
    if (!template) return null;
    const count = GROUPS.reduce((n, g) => n + template[g.key].length, 0);
    if (!template.presetKey || template.presetKey === 'custom') return t('template.meta.custom', { count });
    const preset = t(`template.preset.${template.presetKey}`);
    return isPristinePreset(template)
      ? t('template.meta.presetPristine', { preset, count })
      : t('template.meta.preset', { preset, count });
  }, [template, t]);

  return (
    <section className="mb-8">
      {/* Phone: the meta line drops under the heading instead of squeezing
          the title onto two lines (mock E1 frame 2 keeps the heading whole). */}
      <div className="mb-3 flex flex-wrap items-center gap-x-3 gap-y-1">
        <h2 className="font-display text-[15px] font-semibold tracking-[-0.012em] text-surface-800">{t('template.section')}</h2>
        {meta && <span className="order-last basis-full text-[12.5px] text-surface-500 sm:order-none sm:basis-auto">{meta}</span>}
        {template && (
          <AdminOnlyTooltip show={!canManage} className="ml-auto">
            <button
              type="button"
              disabled={!canManage}
              onClick={() => openWith(template)}
              className="inline-flex items-center gap-1.5 text-[12.5px] font-medium text-brand-600 disabled:opacity-50"
            >
              <Pencil className="h-[13px] w-[13px]" strokeWidth={2} />
              {t('template.edit')}
            </button>
          </AdminOnlyTooltip>
        )}
      </div>

      {template ? (
        <>
          <div className="grid rounded-lg border border-surface-200 bg-surface-0 max-sm:divide-y sm:grid-cols-3 sm:divide-x divide-surface-100">
            {GROUPS.map((g) => (
              <div key={g.key} className="px-4 py-4">
                <p className="mb-2.5 text-[11px] font-semibold uppercase tracking-[0.05em] text-surface-400">
                  {t(`template.group.${g.label}`)}{' '}
                  <span className="font-medium normal-case tracking-normal">· {template[g.key].length}</span>
                </p>
                <div className="flex flex-col gap-2.5">
                  {template[g.key].map((i) => (
                    <div key={i.id}>
                      <p className="text-[13.5px] font-medium text-surface-800">{i.name}</p>
                      {i.hint && <p className="text-[12px] text-surface-500">{i.hint}</p>}
                    </div>
                  ))}
                  {template[g.key].length === 0 && (
                    <p className="text-[12px] text-surface-400">{t('template.group.emptyRow')}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
          <p className="mt-2 text-[12px] text-surface-400">{t('template.applyNote', { subject: subject.name })}</p>
        </>
      ) : (
        <div className="rounded-lg border border-dashed border-brand-300 bg-surface-50 p-4">
          <p className="text-[13.5px] leading-relaxed text-surface-600 text-pretty">{t('template.empty.body')}</p>
          <div className="mt-3 flex flex-col gap-2">
            {NOTE_TEMPLATE_PRESET_ORDER.map((key) => {
              const Icon = PRESET_ICON[key];
              const sub = key === 'custom'
                ? t('template.preset.customSub')
                : NOTE_TEMPLATE_PRESETS[key].studentItems.map((i) => i.name).join(' · ');
              return (
                <AdminOnlyTooltip key={key} show={!canManage}>
                  <button
                    type="button"
                    disabled={!canManage}
                    onClick={() => openWith(key === 'custom' ? EMPTY_CUSTOM : presetWithFreshIds(key))}
                    className="flex min-h-[60px] w-full items-center gap-3 rounded-md border border-surface-200 bg-surface-0 px-3 py-2.5 text-left disabled:opacity-60"
                  >
                    <span className={cn('inline-flex size-[35.2px] shrink-0 items-center justify-center rounded-full', key === 'custom' ? 'bg-surface-100 text-surface-500' : 'bg-brand-50 text-brand-700')}>
                      <Icon className="h-[17px] w-[17px]" strokeWidth={2} />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-[14.5px] font-semibold text-surface-800">{t(`template.preset.${key}`)}</span>
                      <span className="block truncate text-[12.5px] text-surface-500">{sub}</span>
                    </span>
                    <ChevronRight className="h-[18px] w-[18px] shrink-0 text-surface-400" strokeWidth={2} />
                  </button>
                </AdminOnlyTooltip>
              );
            })}
          </div>
          <p className="mt-2.5 text-[12px] text-surface-400">{t('template.empty.hint')}</p>
        </div>
      )}

      {/* THI-346: the session-rating switch. A Subject column, not a template
          field, so a free-text subject can rate too; it therefore renders
          in both branches above. Fixed five levels, nothing else to set. */}
      <div className="mt-3 flex items-start gap-3 rounded-lg border border-surface-200 bg-surface-0 px-4 py-3.5">
        <div className="min-w-0 flex-1">
          <label htmlFor={`subject-${subject.id}-rating`} className="block text-[13.5px] font-medium text-surface-800">
            {t('template.rating.label')}
            <span className="ml-2 text-[12px] font-normal text-surface-500">
              {subject.sessionRatingEnabled ? t('template.rating.on') : t('template.rating.off')}
            </span>
          </label>
          <p className="mt-0.5 text-[12.5px] leading-snug text-surface-500">{t('template.rating.desc')}</p>
        </div>
        <AdminOnlyTooltip show={!canManage}>
          <Switch
            id={`subject-${subject.id}-rating`}
            checked={subject.sessionRatingEnabled}
            disabled={!canManage || updateSubject.isPending}
            onCheckedChange={(checked) =>
              updateSubject.mutate(
                { sessionRatingEnabled: checked },
                { onSuccess: () => toast.success(checked ? t('template.rating.savedOn') : t('template.rating.savedOff')) },
              )
            }
          />
        </AdminOnlyTooltip>
      </div>

      <NoteTemplateEditor subject={subject} open={editorOpen} initial={initial} onOpenChange={setEditorOpen} />
    </section>
  );
}
